import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {I18n} from 'react-redux-i18n';
import classNames from 'classnames';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';

import {withStyles} from '@material-ui/core/styles';

import {widgetKeys} from './constants';
import {INSIGHTS_DECISIONS, ROUTES} from '../../../constants';
import {
  BlocksStats,
  CountStats,
  DashboardOperatorFiltersPanel,
  PerformanceStats,
  RenderDivOrEmpty,
  RequestsStats,
  ThreatStats,
  TopCategoriesCard,
  TopDomainsCard,
} from '../../../components';

import {getDashboardData} from './action';
import {getCampuses} from '../../../app-common/Campus/action';
import {cancelRequests} from '../../../containers/Loading/action';
import {loadProfiles} from '../../../containers/ClientsConfigurer/action';

import style from './style';

class Dashboard extends Component {
  static initialLocalState = {
    selectedPeriodIndex: 0,
    selectedCampusIndex: 0, // Site at index 0 is All Sites. It's different from site in campuses[0]
    selectedProfileIndex: 0,
  };

  static shouldReloadDashboard(prevProps, props) {
    return !_isEqual(prevProps.loggedAccount.accountId, props.loggedAccount.accountId)
      || !_isEqual(
        _get(prevProps.currentOrganization, 'id', null),
        _get(props.currentOrganization, 'id', null),
      );
  }

  constructor(props) {
    super(props);
    this.state = Dashboard.initialLocalState;
    this.top5CategoriesTableHeaders = [
      I18n.t('dashboard.categories.categoryHeader'),
      I18n.t('dashboard.categories.percentageHeader'),
      I18n.t('dashboard.categories.countHeader'),
    ];
    this.top5DomainsTableHeaders = [
      I18n.t('dashboard.domains.domainHeader'),
      I18n.t('dashboard.domains.countHeader'),
    ];
  }

  componentDidMount() {
    this.props.getCampuses();
    this.props.loadProfiles();
  }

  componentDidUpdate(prevProps) {
    if (Dashboard.shouldReloadDashboard(prevProps, this.props)) {
      this.props.getCampuses();
      this.props.loadProfiles();
    }
  }

  componentWillUnmount() {
    this.interruptLoadingRequests(true);
  }

  handleRefresh = (refreshData) => {
    this.interruptLoadingRequests(false);
    this.props.getDashboardData(refreshData);
  };

  interruptLoadingRequests = (includePageKey) => {
    this.props.cancelRequests(Object.values(widgetKeys), includePageKey);
  };

  render() {
    const {
      averageLatency,
      blocksStats,
      campuses,
      profiles,
      classes,
      loggedAccount,
      accountInfo,
      currentOrganization,
      networksStats,
      requestsByDecision,
      requestsPerSecond,
      top5Categories,
      top5Domains,
      userRequestsGraph,
      clientCountStats,
      usersCountStats,
      threats,
    } = this.props;
    const {selectedCampusIndex, selectedPeriodIndex, selectedProfileIndex} = this.state;

    return (
      <div className={classes.dashboard}>
        <div className={classes.dashboard__filtersPanel}>
          <DashboardOperatorFiltersPanel
            campuses={campuses}
            profiles={profiles}
            handleRefresh={this.handleRefresh}
            loggedAccount={loggedAccount}
            currentOrganization={currentOrganization}
            onSelectedPeriodChange={(selectedPeriodIndex) => this.setState({selectedPeriodIndex})}
            onSelectedCampusChange={(selectedCampusIndex) => this.setState({selectedCampusIndex})}
            onSelectedProfileChange={
              (selectedProfileIndex) => this.setState({selectedProfileIndex})
            }
            selectedPeriodIndex={this.state.selectedPeriodIndex}
            selectedCampusIndex={this.state.selectedCampusIndex}
            selectedProfileIndex={this.state.selectedProfileIndex}
          />
        </div>
        {/* Counts and performance widgets */}
        <div className={classes.dashboard__statsRow}>
          <div className={classes.cardBlocks__block}>
            {/* numericCardsClasses */}
            <CountStats
              title={I18n.t('dashboard.statusStats.title')}
              positiveDescription={I18n.t('dashboard.statusStats.networksUp')}
              negativeDescription={I18n.t('dashboard.statusStats.networksDown')}
              positiveValue={networksStats.up}
              negativeValue={networksStats.down}
              widgetKey={widgetKeys.CAMPUSES_STATS}
            />
          </div>
          <div className={classes.cardBlocks__block}>
            <CountStats
              title={I18n.t('dashboard.clientCountStats.title')}
              positiveDescription={I18n.t('dashboard.clientCountStats.online')}
              negativeDescription={I18n.t('dashboard.clientCountStats.offline')}
              positiveValue={clientCountStats.online}
              negativeValue={clientCountStats.offline}
              widgetKey={widgetKeys.CLIENT_STATS}
              url={ROUTES.CLIENTS}
            />
          </div>
          <div className={classes.cardBlocks__block}>
            <CountStats
              title={I18n.t('dashboard.usersCountStats.title')}
              positiveDescription={I18n.t('dashboard.usersCountStats.online')}
              negativeDescription={I18n.t('dashboard.usersCountStats.online')}
              positiveValue={usersCountStats.online}
              negativeValue={usersCountStats.offline}
              widgetKey={widgetKeys.USERS_STATS}
              url={ROUTES.USERS}
            />
          </div>
          <div className={classes.cardBlocks__block}>
            <PerformanceStats
              averageLatency={averageLatency}
              requestsPerSecond={requestsPerSecond}
              widgetKey={widgetKeys.CAMPUSES_STATS}
            />
          </div>
        </div>
        {/* Threats and graph */}
        <div className={classes.dashboard__statsRow}>
          <RenderDivOrEmpty
            isShow={accountInfo.deviceAgentAllowed}
            className={classes.cardBlocks__block_flex1}
          >
            <ThreatStats
              stats={threats}
              widgetKey={widgetKeys.THREAT_STATS}
              canNavigate={true}
              selectedPeriodIndex={selectedPeriodIndex}
              selectedCampusId={campuses[selectedCampusIndex - 1]?.id}
              campuses={campuses}
              selectedProfileId={profiles[selectedProfileIndex - 1]?.id}
              profiles={profiles}
            />
          </RenderDivOrEmpty>
          <div className={classes.cardBlocks__block_flex2}>
            <RequestsStats
              userRequestsGraph={userRequestsGraph}
              requestsAllowed={requestsByDecision.ALLOWED}
              requestsBlocked={requestsByDecision.FORBIDDEN}
              requestsTotal={requestsByDecision.ALLOWED + requestsByDecision.FORBIDDEN}
              widgetKey={widgetKeys.REQUEST_STATS}
            />
          </div>
        </div>
        {/* Isn't shown. Maybe need to remove? */}
        <div>
          <div
            className={classNames(
              classes.stats__block_numeric,
              classes.stats__block_bottom,
            )}
          >
            <BlocksStats
              applications={blocksStats.applications}
              content={blocksStats.content}
              whiteBlackList={blocksStats.whiteBlackList}
              threats={blocksStats.threats}
              widgetKey={widgetKeys.BLOCKED_STATS}
            />
          </div>
        </div>
        {/* Top categories */}
        <div className={classes.dashboard__statsRow}>
          <div className={classes.cardBlocks__block_flex1}>
            <TopCategoriesCard
              classes={classes}
              chartData={top5Categories.ALLOWED.chartData}
              headers={this.top5CategoriesTableHeaders}
              textWhenDataNotAvailable={I18n.t('dashboard.categories.dataNotAvailable')}
              title={I18n.t('dashboard.categories.allowedTitle')}
              label={I18n.t('dashboard.categories.allowedLabel')}
              titleColor="green"
              tableData={top5Categories.ALLOWED.tableData}
              widgetKey={widgetKeys.TOP_CATEGORIES_ALLOWED}
              insightsFilterValue={INSIGHTS_DECISIONS.ALLOWED}
              selectedPeriodIndex={selectedPeriodIndex}
              selectedCampusId={campuses[selectedCampusIndex - 1]?.id}
              campuses={campuses}
              selectedProfileId={profiles[selectedProfileIndex - 1]?.id}
              profiles={profiles}
            />
          </div>
          <div className={classes.cardBlocks__block_flex1}>
            <TopCategoriesCard
              classes={classes}
              chartData={top5Categories.FORBIDDEN.chartData}
              headers={this.top5CategoriesTableHeaders}
              textWhenDataNotAvailable={I18n.t('dashboard.categories.dataNotAvailable')}
              title={I18n.t('dashboard.categories.blockedTitle')}
              label={I18n.t('dashboard.categories.blockedLabel')}
              titleColor="red"
              tableData={top5Categories.FORBIDDEN.tableData}
              widgetKey={widgetKeys.TOP_CATEGORIES_FORBIDDEN}
              insightsFilterValue={INSIGHTS_DECISIONS.FORBIDDEN}
              selectedPeriodIndex={selectedPeriodIndex}
              selectedCampusId={campuses[selectedCampusIndex - 1]?.id}
              campuses={campuses}
              selectedProfileId={profiles[selectedProfileIndex - 1]?.id}
              profiles={profiles}
            />
          </div>
        </div>
        {/* Top domains */}
        <div className={classes.dashboard__statsRow}>
          <div className={classes.cardBlocks__block_flex1}>
            <TopDomainsCard
              classes={classes}
              headers={this.top5DomainsTableHeaders}
              tableData={top5Domains.ALLOWED.tableData}
              textWhenDataNotAvailable={I18n.t('dashboard.domains.dataNotAvailable')}
              title={I18n.t('dashboard.domains.allowedTitle')}
              titleColor="green"
              widgetKey={widgetKeys.TOP_DOMAINS_ALLOWED}
              insightsFilterValue={INSIGHTS_DECISIONS.ALLOWED}
              selectedPeriodIndex={selectedPeriodIndex}
              selectedCampusId={campuses[selectedCampusIndex - 1]?.id}
              campuses={campuses}
              selectedProfileId={profiles[selectedProfileIndex - 1]?.id}
              profiles={profiles}
            />
          </div>
          <div className={classes.cardBlocks__block_flex1}>
            <TopDomainsCard
              classes={classes}
              headers={this.top5DomainsTableHeaders}
              tableData={top5Domains.FORBIDDEN.tableData}
              textWhenDataNotAvailable={I18n.t('dashboard.domains.dataNotAvailable')}
              title={I18n.t('dashboard.domains.blockedTitle')}
              titleColor="red"
              widgetKey={widgetKeys.TOP_DOMAINS_FORBIDDEN}
              insightsFilterValue={INSIGHTS_DECISIONS.FORBIDDEN}
              selectedPeriodIndex={selectedPeriodIndex}
              selectedCampusId={campuses[selectedCampusIndex - 1]?.id}
              campuses={campuses}
              selectedProfileId={profiles[selectedProfileIndex - 1]?.id}
              profiles={profiles}
            />
          </div>
        </div>
      </div>
    );
  }
}

Dashboard.propTypes = {
  averageLatency: PropTypes.number.isRequired,
  blocksStats: PropTypes.object.isRequired,
  campuses: PropTypes.array.isRequired,
  profiles: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  loggedAccount: PropTypes.object.isRequired,
  accountInfo: PropTypes.object.isRequired,
  currentOrganization: PropTypes.object,
  networksStats: PropTypes.object.isRequired,
  requestsByDecision: PropTypes.object.isRequired,
  requestsPerSecond: PropTypes.number.isRequired,
  top5Categories: PropTypes.object.isRequired,
  top5Domains: PropTypes.object.isRequired,
  userRequestsGraph: PropTypes.object.isRequired,
  threats: PropTypes.array.isRequired,
  clientCountStats: PropTypes.object.isRequired,
  usersCountStats: PropTypes.object.isRequired,

  cancelRequests: PropTypes.func.isRequired,
  getDashboardData: PropTypes.func.isRequired,
  getCampuses: PropTypes.func.isRequired,
  loadProfiles: PropTypes.func.isRequired,
};

Dashboard.defaultProps = {
  currentOrganization: null,
};

const mapStateToProps = (state) => ({
  campuses: state.campusReducer.campuses,
  profiles: state.clientsConfigurerReducer.profiles,

  loggedAccount: state.userAccountsReducer.loggedAccount,
  accountInfo: state.operatorLayoutReducer.accountInfo,
  currentOrganization: state.userOrganizationsReducer.currentOrganization,

  averageLatency: state.dashboardReducer.averageLatency,
  blocksStats: state.dashboardReducer.blocksStats,
  networksStats: state.dashboardReducer.networksStats,
  requestsByDecision: state.dashboardReducer.requestsByDecision,
  requestsPerNetwork: state.dashboardReducer.requestsPerNetwork,
  requestsPerSecond: state.dashboardReducer.requestsPerSecond,
  top5Categories: state.dashboardReducer.top5Categories,
  top5Domains: state.dashboardReducer.top5Domains,
  userRequestsGraph: state.dashboardReducer.userRequestsGraph,
  threats: state.dashboardReducer.threats,
  clientCountStats: state.dashboardReducer.clientCountStats,
  usersCountStats: state.dashboardReducer.usersCountStats,
});

const mapDispatchToProps = (dispatch) => ({
  getDashboardData: bindActionCreators(getDashboardData, dispatch),
  getCampuses: bindActionCreators(getCampuses, dispatch),
  cancelRequests: bindActionCreators(cancelRequests, dispatch),
  loadProfiles: bindActionCreators(loadProfiles, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(style)(Dashboard));
